import * as Yup from 'yup';
import history from 'browserHistory';

import { PhoneService } from '../../../../../entities/Phone/service';
import { TripStatusIds, tripStatuses } from '../../../../../entities/Trip';

const getValidationSchema = (status: TripStatusIds | number, isLoading: boolean, useVisitorDoc: boolean = true) => {
  const pathMode = history.location.pathname.split('/')[1];

  const isSecurity = pathMode === 'security';
  const isOrdering = pathMode === 'ordering';
  const isDirector = pathMode === 'director';
  const isNew =
    status === tripStatuses.create || status === tripStatuses.parkingInLine || status === tripStatuses.unloading;
  const isSecurityNew = isSecurity && isNew;

  return Yup.object().shape(
    {
      driver: Yup.object().shape(
        {
          phone: Yup.object().shape({
            number: (() => {
              let validation = Yup.string().test('len', 'Поле "Телефон" повинно містити 12 символів', (value = '') => {
                const changedValue = PhoneService.unformatPhone(value);
                return changedValue.length === 12;
              });
              if (isSecurityNew) {
                validation = validation.required('Поле "Телефон" обов\'язкове');
              }
              return validation;
            })(),
          }),
          lastName: (() => {
            let validation = Yup.string()
              .trim('В полі не мають бути пробіли між літерами')
              .nullable()
              .matches(/^[a-zA-ZА-ЩЬЮЯҐЄІЇа-щьюяґєії\- ']+$/, 'Поле "Прізвище" повинно містити тільки літери');
            if (isSecurityNew) {
              validation = validation.required('Поле "Прізвище" обов\'язкове');
            }
            return validation;
          })(),
          firstName: (() => {
            let validation = Yup.string()
              .trim('В полі не мають бути пробіли між літерами')
              .nullable()
              .matches(/^[a-zA-ZА-ЩЬЮЯҐЄІЇа-щьюяґєії\- ']+$/, 'Поле "Ім\'я" повинно містити тільки літери');
            if (isSecurityNew) {
              validation = validation.required('Поле "Ім\'я" обов\'язкове');
            }
            return validation;
          })(),
          middleName: (() => {
            let validation = Yup.string()
              .trim('В полі не мають бути пробіли між літерами')
              .nullable()
              .matches(/^[a-zA-ZА-ЩЬЮЯҐЄІЇа-щьюяґєії\- ']+$/, 'Поле "По батькові" повинно містити тільки літери');
            if (isSecurityNew) {
              validation = validation.required('Поле "По батькові" обов\'язкове');
            }
            return validation;
          })(),
          documents: Yup.array().of(
            Yup.object().shape({
              type: Yup.number(),
              number: Yup.string()
                .nullable()
                .when(['type'], {
                  is: (d: any) => d !== 3 && (d !== 1 || useVisitorDoc),
                  then: Yup.string().min(2, "Обов'язкове поле").required("Обов'язкове поле"),
                }),
              pictures: Yup.array().of(Yup.mixed()),
              pictureFiles: Yup.array()
                .of(Yup.mixed())
                .when(['number', 'pictures'], {
                  is: (number: string, pictures: any) =>
                    number && pictures && pictures.filter((p: any) => !p.isDeleted).length < 2,

                  then: (() => {
                    const validation = Yup.array().of(Yup.mixed());
                    if (isOrdering) {
                      return validation.required('Загрузіть не менше 2-x фото документу');
                    }
                    if (isSecurity && status !== 1) {
                      return validation.required('Загрузіть не менше 2-x фото документу');
                    }

                    return validation;
                  })(),
                })
                .when(['number', 'pictures'], {
                  is: (number: string, pictures: any) => number && (!pictures || !pictures.length),
                  then: (() => {
                    const validation = Yup.array().of(Yup.mixed());

                    if (isOrdering || isDirector) {
                      return validation
                        .min(2, 'Загрузіть не менше 2-x фото документу')
                        .required('Загрузіть не менше 2-x фото документу');
                    }
                    if (isSecurity && +status !== 1) {
                      return validation
                        .min(2, 'Загрузіть не менше 2-x фото документу')
                        .required('Загрузіть не менше 2-x фото документу');
                    }
                    return Yup.array();
                  })(),
                }),
            }),
          ),
        },
        [
          ['type', 'number'],
          ['type', 'pictureFiles'],
          ['number', 'type'],
          ['number', 'pictures'],
          ['number', 'pictureFiles'],
          ['pictures', 'pictureFiles'],
        ],
      ),
      isDriverDocumentValid: (() => {
        const validation = Yup.boolean();

        if (isOrdering || (!isSecurity && !isNew) || isDirector) {
          return validation
            .oneOf([true], 'Підтвердіть верифікацію')
            .required('Поле "Документи верифіковано" обов\'язкове');
        }
        return validation;
      })(),
      ...(isLoading
        ? {
            forwarder: Yup.object().shape(
              {
                phone: Yup.object()
                  .shape({
                    number: Yup.string().test('len', 'Поле "Телефон" повинно містити 12 символів', (value = '') => {
                      const changedValue = PhoneService.unformatPhone(value);
                      return changedValue.length === 12 || changedValue.length === 3 || !changedValue.length;
                    }),
                  })
                  .nullable()
                  .when(['lastName', 'firstName', 'middleName', 'documents[0].number'], {
                    is: (lName: string, fName: string, mName: string, dNumber: string) =>
                      !!fName || !!mName || !!lName || !!dNumber,
                    then: Yup.object()
                      .shape({
                        number: Yup.string()
                          .test('len', 'Поле "Телефон" повинно містити 12 символів', (value = '') => {
                            const changedValue = PhoneService.unformatPhone(value);
                            return changedValue.length === 12 || !changedValue.length;
                          })
                          .required('Поле "Телефон" обов\'язкове'),
                      })
                      .nullable(),
                  }),
                lastName: Yup.string()
                  .nullable()
                  .trim('В полі не мають бути пробіли між літерами')
                  .matches(
                    /^$|^[a-zA-ZА-ЩЬЮЯҐЄІЇа-щьюяґєії\- ']+$/,
                    'Експедитор. Поле "Прізвище" повинно містити тільки літери',
                  )
                  .when(['phone.number', 'firstName', 'middleName', 'documents[0].number'], {
                    is: (phone: string, fName: string, mName: string, dNumber: string) => {
                      const changedValue = PhoneService.unformatPhone(phone || '');
                      return changedValue?.length > 3 || mName || fName || dNumber;
                    },
                    then: Yup.string().nullable().required('Поле "Прізвище" обов\'язкове'),
                  })
                  .nullable(),
                firstName: Yup.string()
                  .trim('В полі не мають бути пробіли між літерами')
                  .nullable()
                  .matches(/^$|^[a-zA-ZА-ЩЬЮЯҐЄІЇа-щьюяґєії\- ']+$/, 'Поле "Ім\'я" повинно містити тільки літери')
                  .when(['phone.number', 'lastName', 'middleName', 'documents[0].number'], {
                    is: (phone: string, lName: string, mName: string, dNumber: string) => {
                      const changedValue = PhoneService.unformatPhone(phone || '');
                      return changedValue?.length > 3 || mName || lName || dNumber;
                    },
                    then: Yup.string().nullable().required('Поле "Ім\'я" обов\'язкове'),
                  })
                  .nullable(),
                middleName: Yup.string()
                  .trim('В полі не мають бути пробіли між літерами')
                  .nullable()
                  .matches(/^$|^[a-zA-ZА-ЩЬЮЯҐЄІЇа-щьюяґєії\- ']+$/, 'Поле "По батькові" повинно містити тільки літери')
                  .when(['phone.number', 'lastName', 'firstName', 'documents[0].number'], {
                    is: (phone: string, lName: string, fName: string, dNumber: string) => {
                      const changedValue = PhoneService.unformatPhone(phone || '');
                      return changedValue?.length > 3 || fName || lName || dNumber;
                    },
                    then: Yup.string().nullable().required('Поле "По батькові" обов\'язкове'),
                  })
                  .nullable(),
                documents: Yup.array()
                  .of(
                    Yup.object().shape({
                      number: Yup.string().nullable(),
                      pictures: Yup.array().of(Yup.mixed()),
                      pictureFiles: Yup.array().of(Yup.mixed()),
                    }),
                  )
                  .when(['phone.number', 'lastName', 'firstName', 'middleName'], {
                    is: (phone: string, lName: string, fName: string, mName: string) => {
                      const changedValue = PhoneService.unformatPhone(phone || '');
                      return changedValue?.length > 3 || fName || lName || mName;
                    },
                    then: Yup.array().of(
                      Yup.object().shape({
                        number: Yup.string().nullable().required('Поле "Номер паспорту" обов\'язкове'),
                        pictures: Yup.array().of(Yup.mixed()),
                        pictureFiles: Yup.array()
                          .of(Yup.mixed())
                          .when(['number', 'pictures'], {
                            is: (number: string, pictures: any) =>
                              number && pictures && pictures.filter((p: any) => !p.isDeleted).length < 2,
                            then: (() => {
                              let validation = Yup.array().of(Yup.mixed());

                              if (isOrdering || isDirector) {
                                validation = validation.required('Загрузіть не менше 2-x фото документу (експедитор)');
                              }

                              if ((isSecurity && status !== 1) || (isSecurityNew && !isLoading)) {
                                validation = validation.required('Загрузіть не менше 2-x фото документу (експедитор)');
                              }

                              return validation;
                            })(),
                          })
                          .when(['number', 'pictures'], {
                            is: (number: string, pictures: any) => number && (!pictures || !pictures.length),
                            then: (() => {
                              let validation = Yup.array().of(Yup.mixed());

                              if (isOrdering || isDirector) {
                                validation = validation
                                  .min(2, 'Загрузіть не менше 2-x фото документу (експедитор.)')
                                  .required('Загрузіть не менше 2-x фото документу (експедитор.)');
                              }

                              if (isSecurityNew && !isLoading) {
                                validation = validation
                                  .min(2, 'Загрузіть не менше 2-x фото документу (експедитор..)')
                                  .required('Загрузіть не менше 2-x фото документу (експедитор..)');
                              }

                              return validation;
                            })(),
                          }),
                      }),
                    ),
                  }),
              },
              [
                ['phone', 'lastName'],
                ['phone', 'firstName'],
                ['phone', 'middleName'],
                ['phone', 'documents'],
                ['lastName', 'firstName'],
                ['lastName', 'middleName'],
                ['lastName', 'documents'],
                ['firstName', 'middleName'],
                ['firstName', 'documents'],
                ['middleName', 'documents'],
                ['number', 'pictures'],
              ],
            ),
          }
        : {}),

      isForwarderDocumentValid: Yup.boolean().when('forwarder', {
        is: ({ lastName, firstName, middleName }: any) => !!lastName || !!firstName || !!middleName,
        then: (() => {
          let validation = Yup.boolean();

          if (isOrdering || isDirector) {
            validation = validation
              .oneOf([true], 'Підтвердіть верифікацію')
              .required('Поле "Документи верифіковано" обов\'язкове');
          }

          return validation;
        })(),
      }),
    },
    [
      ['forwarder', 'isForwarderDocumentValid'],
      ['driver', 'tripIntention'],
      ['forwarder', 'tripIntention'],
    ],
  );
};

export default getValidationSchema;
